import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  deletePlatformV1OrganizationsId,
  deletePlatformV1SupportSessionsId,
  getPlatformV1Organizations,
  getPlatformV1OrganizationsId,
  getPlatformV1OrganizationsIdExportJobId,
  getPlatformV1OrganizationsIdUsage,
  getPlatformV1SupportSessions,
  patchPlatformV1OrganizationsId,
  patchPlatformV1OrganizationsIdOwnerInvite,
  postPlatformV1Organizations,
  postPlatformV1OrganizationsIdClose,
  postPlatformV1OrganizationsIdExport,
  postPlatformV1OrganizationsIdReactivate,
  postPlatformV1OrganizationsIdReopen,
  postPlatformV1OrganizationsIdResendOwnerInvite,
  postPlatformV1OrganizationsIdSupportSession,
  postPlatformV1OrganizationsIdSuspend,
  type CloseOrganizationRequest,
  type CreateOrganizationRequest,
  type CreateOrganizationResponse,
  type GetPlatformV1Organizations200,
  type GetPlatformV1SupportSessions200,
  type GetPlatformV1SupportSessionsParams,
  type Organization,
  type OrganizationDetail,
  type OrganizationExportJob,
  type OrganizationListItem,
  type OrganizationUsage,
  type OwnerInviteSent,
  type SupportSessionStarted,
  type SuspendOrganizationRequest,
  type UpdateOrganizationRequest,
} from '@nucleus-commerce/api-client/platform';

/** Backend maximum page size. */
export const MAX_PAGE_SIZE = 100;
/** Hard stop for the count-everything walk; 1,000 organizations is far beyond the ~20 planned. */
export const MAX_PAGES = 10;

export interface OrganizationWalk {
  items: OrganizationListItem[];
  truncated: boolean;
}

/** API layer: thin, typed wrappers over the generated client. Responses are bare objects. */
export const organizationsApi = {
  /** `params` as `organizationListParams` builds them: `q`, `sort`, `status`, `createdAt_gte`... */
  list: (params: ListParams): Promise<GetPlatformV1Organizations200> =>
    getPlatformV1Organizations(params),
  /** Follows nextCursor with the largest page size until the end or MAX_PAGES. */
  listAll: async (): Promise<OrganizationWalk> => {
    const items: OrganizationListItem[] = [];
    let cursor: string | undefined;
    for (let page = 0; page < MAX_PAGES; page += 1) {
      const result = await getPlatformV1Organizations({
        limit: MAX_PAGE_SIZE,
        ...(cursor ? { cursor } : {}),
      });
      items.push(...result.items);
      if (result.nextCursor === null) return { items, truncated: false };
      cursor = result.nextCursor;
    }
    return { items, truncated: true };
  },
  get: (organizationId: string): Promise<OrganizationDetail> =>
    getPlatformV1OrganizationsId({ id: organizationId }),
  provision: (input: CreateOrganizationRequest): Promise<CreateOrganizationResponse> =>
    postPlatformV1Organizations(input),
  update: (organizationId: string, input: UpdateOrganizationRequest): Promise<Organization> =>
    patchPlatformV1OrganizationsId({ id: organizationId }, input),
  usage: (organizationId: string): Promise<OrganizationUsage> =>
    getPlatformV1OrganizationsIdUsage({ id: organizationId }),
  suspend: (organizationId: string, input: SuspendOrganizationRequest): Promise<Organization> =>
    postPlatformV1OrganizationsIdSuspend({ id: organizationId }, input),
  reactivate: (organizationId: string): Promise<Organization> =>
    postPlatformV1OrganizationsIdReactivate({ id: organizationId }),
  close: (organizationId: string, input: CloseOrganizationRequest): Promise<Organization> =>
    postPlatformV1OrganizationsIdClose({ id: organizationId }, input),
  reopen: (organizationId: string): Promise<Organization> =>
    postPlatformV1OrganizationsIdReopen({ id: organizationId }),
  remove: (organizationId: string): Promise<void> =>
    deletePlatformV1OrganizationsId({ id: organizationId }),
  resendOwnerInvite: (organizationId: string): Promise<OwnerInviteSent> =>
    postPlatformV1OrganizationsIdResendOwnerInvite({ id: organizationId }),
  updateOwnerInvite: (organizationId: string, email: string): Promise<OwnerInviteSent> =>
    patchPlatformV1OrganizationsIdOwnerInvite({ id: organizationId }, { email }),
  startExport: (organizationId: string): Promise<OrganizationExportJob> =>
    postPlatformV1OrganizationsIdExport({ id: organizationId }),
  getExport: (organizationId: string, jobId: string): Promise<OrganizationExportJob> =>
    getPlatformV1OrganizationsIdExportJobId({ id: organizationId, jobId }),
  startSupportSession: (organizationId: string, reason: string): Promise<SupportSessionStarted> =>
    postPlatformV1OrganizationsIdSupportSession({ id: organizationId }, { reason }),
  supportSessions: (
    params: GetPlatformV1SupportSessionsParams,
  ): Promise<GetPlatformV1SupportSessions200> => getPlatformV1SupportSessions(params),
  endSupportSession: (supportSessionId: string): Promise<void> =>
    deletePlatformV1SupportSessionsId({ id: supportSessionId }),
};
